import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { 
  Github, 
  Linkedin, 
  Mail, 
  ExternalLink, 
  Code2, 
  Briefcase, 
  Award, 
  ChevronDown,
  Terminal,
  Database,
  Cpu,
  Cloud,
  Bot,
  Wallet,
  TrendingUp,
  X
} from 'lucide-react';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import ParticleBackground from './components/ParticleBackground';

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const [typedText, setTypedText] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);

  const roles = ['Backend/Python Developer', 'AI Engineer', 'Full Stack AI Engineer'];
  const [currentRole, setCurrentRole] = useState(0);
  
  // Scroll detection for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    const roleText = roles[currentRole];
    let index = 0;
    
    const typingInterval = setInterval(() => {
      if (index <= roleText.length) {
        setTypedText(roleText.substring(0, index));
        index++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setCurrentRole((prev) => (prev + 1) % roles.length);
        }, 2000);
      }
    }, 100);
    
    return () => clearInterval(typingInterval);
  }, [currentRole]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: sectionId === 'home' ? 0 : offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Projects data with full details - 6 Featured Projects
  const projects = [
    {
      id: 1,
      title: 'Gaming Faction AI Agents',
      category: 'AI/Web3',
      icon: Bot,
      shortDesc: 'Autonomous Twitter AI agents with TypeScript ElizaOS framework for gaming anime characters',
      fullDesc: 'Built faction-based Twitter bots (@TsailanLeader, @GaianLeader) using ElizaOS TypeScript framework. ElizaOS is a TypeScript framework for building AI agents that can think, learn, and act autonomously. Created agents with unique, persistent personalities using JSON character files, equipped with plugins to interact with the world through automated posts and intelligent replies.',
      overview: 'Developed 2 gaming anime character AI agents with complete personality configurations, automated posting systems, and intelligent engagement capabilities. Each agent has unique bio, lore, knowledge base, and communication style defined in JSON configuration files integrated with TypeScript code.',
      tech: ['TypeScript', 'ElizaOS Framework', 'Twitter API', 'Groq AI (gpt-4o)', 'Solana Web3', 'JSON Configuration'],
      achievements: [
        'Created 2 character-driven AI agents: Captain Silas Kaine (Tsalian) and Commander Elysia Voss (Gaian)',
        'Implemented automated posting with time-based intervals related to character storylines',
        'Built intelligent reply system - agents can respond to mentions and comments contextually',
        'Integrated blockchain actions: staking, trading tokens, lending assets, checking balances',
        'Designed complete personality systems with bio, lore, knowledge, message examples, and communication styles',
        'Voice model integration for character-specific audio responses'
      ],
      features: [
        'Persistent personality with JSON character configuration',
        'Automated tweet generation based on character themes',
        'Contextual reply system for user engagement',
        'Blockchain wallet integration (Solana)',
        'Role-playing system with predefined topics and hashtags'
      ],
      gradient: 'from-cyan-500 to-emerald-500'
    },
    {
      id: 2,
      title: 'Solana Wallet Management System',
      category: 'Blockchain',
      icon: Wallet,
      shortDesc: 'Automated multi-wallet management tool with intelligent fund distribution and real-time alerts',
      fullDesc: 'Created automated Solana wallet management tool with multi-wallet support, intelligent fund distribution, and Slack/Discord webhook notifications.',
      tech: ['Python', 'Solana', 'Web3.py', 'Slack/Discord API', 'Async Programming'],
      achievements: [
        'Threshold-based rebalancing with dynamic keypair management',
        'Multi-wallet fund combining when single wallet insufficient',
        'RPC fallback system with rotation for 99.9% uptime',
        'Rate-limited notifications preventing spam'
      ],
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      id: 3,
      title: 'AI Voice Assistant Platform',
      category: 'AI/Full-Stack',
      icon: Bot,
      shortDesc: 'Voice-enabled chatbot with speech-to-text, AI responses, and text-to-speech integration',
      fullDesc: 'Developed modern voice-enabled chatbot built with FastAPI, React, powered by GroqCloud AI (LLaMA-3.3-70b + Whisper).',
      tech: ['React 19', 'FastAPI', 'Groq AI', 'Whisper', 'gTTS', 'Python'],
      achievements: [
        'Deployed on Render (frontend + backend)',
        'Sub-2-second response times with 5 personality presets',
        'Voice input/output with automatic audio playback',
        'Zero-setup user experience - instant access'
      ],
      gradient: 'from-orange-500 to-pink-500',
      link: 'https://ai-voice-assistant-frontend-rsef.onrender.com'
    },
    {
      id: 4,
      title: 'Doctor Booking Consultation System',
      category: 'HealthTech',
      icon: Briefcase,
      shortDesc: 'Medical insurance-based system with Razorpay integration and real-time ticketing',
      fullDesc: 'Built comprehensive QR payment module with Razorpay integration for medical consultation booking platform.',
      tech: ['React', 'Node.js', 'Razorpay API', 'Azure Cosmos DB', 'Figma'],
      achievements: [
        '10% improvement in transaction success rates',
        '25% enhancement in payment handling efficiency',
        'Optimized backend architecture reducing processing time by 15%',
        'Real-time updates for improved user satisfaction'
      ],
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      id: 5,
      title: 'Twitter Crypto Engagement Bot',
      category: 'AI/Web3',
      icon: X,
      shortDesc: 'AI-powered Twitter bot monitoring crypto hashtags with intelligent engagement',
      fullDesc: 'Python-based Twitter bot that automatically monitors and responds to crypto-related tweets using AI-generated responses with Mixtral-8x7B model.',
      tech: ['Python', 'Twitter API', 'Hugging Face', 'Mixtral-8x7B', 'Async'],
      achievements: [
        'Monitors multiple crypto hashtags (#BTC, #ETH, #CryptoNews)',
        'AI-powered responses with multiple styles (friendly, sarcastic)',
        'Smart rate limit handling with visual countdown',
        'Detailed logging and error management'
      ],
      gradient: 'from-purple-500 to-indigo-500'
    },
    {
      id: 7,
      title: 'AI Recruitment Platform',
      category: 'AI/HR Tech',
      icon: Briefcase,
      shortDesc: 'Automated recruitment system with Meta API integration and AI chatbot screening',
      fullDesc: 'Built comprehensive AI-powered recruitment system with Facebook page integration, automated posting to groups, and AI chatbot for candidate screening.',
      tech: ['Python', 'Meta Graph API', 'N8N', 'AI Chatbots', 'ElevenLabs', 'Vapi.ai'],
      achievements: [
        'Automated job posting to multiple Facebook groups',
        'AI chatbot screening with resume collection',
        'Automatic candidate ranking (Best/Moderate/Least Fit)',
        'HR dashboard with applicant tracking and data export'
      ],
      gradient: 'from-blue-500 to-indigo-500'
    }
  ];

  const skills = {
    'Frontend': ['React.js', 'TypeScript', 'JavaScript', 'Vite.js', 'Tailwind CSS', 'Lucide', 'Recharts'],
    'Backend': ['Python', 'FastAPI', 'Node.js', 'Express.js', 'REST APIs'],
    'Blockchain': ['Solana', 'Ethereum', 'Web3', 'DeFi', 'Helius RPC', 'Solscan', 'Wallet Integration'],
    'AI/ML': ['OpenAI SDK', 'GroqCloud', 'Hugging Face', 'LangChain', 'CrewAI', 'Ollama', 'Streamlit', 'NotebookLLM', 'Agno Library', 'LLM Fine-tuning', 'QLoRA', 'PEFT', 'MergeKit'],
    'Databases': ['MongoDB', 'PostgreSQL', 'SQLite 3', 'Azure Cosmos DB'],
    'Cloud & DevOps': ['AWS S3', 'S3 Object Storage', 'Boto SDK', 'Docker', 'GCP Cloud', 'Azure'],
    'Tools': ['Git', 'Jira', 'Postman', 'Figma', 'N8N', 'Cursor IDE', 'PyCharm']
  };

  const certifications = [
    'Google Associate Cloud Engineer',
    'Oracle Cloud Infrastructure Certified Architect Associate',
    'Oracle Cloud Infrastructure Certified Foundations',
    'Generative AI Leader Certification',
    'Introduction to Prompt Engineering for Generative AI',
    'Python Course: Mastering the Essentials'
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100 relative overflow-hidden">
      <ParticleBackground />
      
      {/* Navigation Bar */}
      <motion.nav 
        className={`fixed top-0 left-0 right-0 z-[200] transition-all duration-300 ${
          scrolled 
            ? 'bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-gray-800 shadow-2xl shadow-emerald-500/10' 
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div 
              className="flex items-center gap-3 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => scrollToSection('home')}
            >
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <span className="text-white font-bold text-2xl">V</span>
              </div>
              <span className="text-2xl font-bold hidden sm:block">
                <span className="text-white">arun.</span>
                <span className="text-emerald-500">dev</span>
              </span>
            </motion.div>
            
            {/* Navigation Links */}
            <div className="flex items-center gap-2 md:gap-8">
              <motion.button
                onClick={() => scrollToSection('home')}
                className="text-gray-300 hover:text-emerald-400 transition-colors px-4 py-2 rounded-lg hover:bg-emerald-500/10 font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Home
              </motion.button>
              
              <motion.button
                onClick={() => scrollToSection('about')}
                className="text-gray-300 hover:text-cyan-400 transition-colors px-4 py-2 rounded-lg hover:bg-cyan-500/10 font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                About Me
              </motion.button>
              
              <motion.button
                onClick={() => scrollToSection('projects')}
                className="text-gray-300 hover:text-orange-400 transition-colors px-4 py-2 rounded-lg hover:bg-orange-500/10 font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Projects
              </motion.button>
              
              <motion.button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white px-6 py-2 rounded-full font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>
      
      {/* Hero Section */}
      <motion.section 
        id="home"
        style={{ opacity, scale }}
        className="min-h-screen flex items-center justify-center relative z-10 px-4 py-20 pt-32"
      >
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left side - Text content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-left"
            >
              <motion.p
                className="text-lg text-gray-400 mb-4 font-light"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
              >
                Hi! I Am
              </motion.p>
              
              <motion.h1 
                className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.6 }}
              >
                <span className="text-white">Varun </span>
                <span className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 bg-clip-text text-transparent">Sharma</span>
              </motion.h1>
              
              <motion.p
                className="text-xl md:text-2xl text-gray-400 mb-10 leading-relaxed font-light"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                I am a <span className="text-emerald-400 font-semibold min-h-[32px] inline-block">{typedText}<span className="animate-pulse">|</span></span> with interest & love in AI/ML things.
              </motion.p>
              
              <motion.div
                className="flex gap-6 items-center flex-wrap"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1 }}
              >
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white border-0 text-lg px-10 py-7 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50 font-semibold"
                  onClick={() => document.getElementById('projects').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hire-me-btn"
                >
                  Hire Me
                </Button>
                
                <div className="flex gap-4">
                  <motion.a
                    href="mailto:varunsharma6956@gmail.com"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-14 h-14 rounded-full bg-[#1a1a1a] border-2 border-gray-800 flex items-center justify-center hover:border-emerald-500 hover:bg-emerald-500/10 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30"
                    data-testid="email-link"
                  >
                    <Mail className="w-6 h-6 text-emerald-400" />
                  </motion.a>
                  
                  <motion.a
                    href="https://www.linkedin.com/in/varun-sharma-00a2b518a/"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-14 h-14 rounded-full bg-[#1a1a1a] border-2 border-gray-800 flex items-center justify-center hover:border-cyan-500 hover:bg-cyan-500/10 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/30"
                    data-testid="linkedin-link"
                  >
                    <Linkedin className="w-6 h-6 text-cyan-400" />
                  </motion.a>
                  
                  <motion.a
                    href="https://github.com/varunsharma6956"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-14 h-14 rounded-full bg-[#1a1a1a] border-2 border-gray-800 flex items-center justify-center hover:border-orange-500 hover:bg-orange-500/10 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/30"
                    data-testid="github-link"
                  >
                    <Github className="w-6 h-6 text-orange-400" />
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Right side - Circular Photo */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex justify-center items-center relative group"
            >
              <div className="relative">
                {/* Decorative glow */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-500/30 via-cyan-500/30 to-orange-500/30 blur-3xl animate-pulse"></div>
                
                {/* Hover Info Card - appears on hover */}
                <motion.div
                  className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-emerald-500/50 rounded-2xl px-6 py-4 shadow-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap z-20"
                >
                  <p className="text-emerald-400 font-semibold text-sm">Backend/Python Developer</p>
                  <p className="text-cyan-400 font-semibold text-sm">AI/ML Engineer</p>
                  <p className="text-orange-400 font-semibold text-sm">Full Stack AI Engineer</p>
                </motion.div>
                
                {/* Main photo circle with rings */}
                <div className="relative">
                  {/* Animated outer ring */}
                  <motion.div 
                    className="absolute -inset-6 rounded-full border-4 border-emerald-500/30"
                    animate={{ rotate: 360, scale: [1, 1.05, 1] }}
                    transition={{ rotate: { repeat: Infinity, duration: 20, ease: "linear" }, scale: { repeat: Infinity, duration: 3 } }}
                  ></motion.div>
                  
                  {/* Middle ring */}
                  <div className="absolute -inset-3 rounded-full bg-gradient-to-r from-cyan-500/40 to-emerald-500/40"></div>
                  
                  {/* Photo container */}
                  <div className="relative w-80 h-80 md:w-[420px] md:h-[420px] rounded-full overflow-hidden border-4 border-[#0a0a0a] shadow-2xl">
                    <img 
                      src={encodeURI('/WhatsApp Image 2024-11-30 at 12.49.56_e9832f5a.jpg')}
                      alt="Varun Sharma"
                      className="w-full h-full object-cover scale-110"
                    />
                  </div>
                  
                  {/* Floating badges removed as requested; hover card above is kept */}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 relative z-10" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold mb-16 text-center bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              About Me
            </h2>
            
            {/* Experience Cards - Full Width */}
            <div className="space-y-6">
              <motion.div
                className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0f0f0f] border-2 border-emerald-500/40 rounded-2xl p-8 hover:border-emerald-500/70 transition-all duration-300 shadow-2xl shadow-emerald-900/20 hover:shadow-emerald-500/30"
                whileHover={{ scale: 1.01, y: -5 }}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-emerald-400 mb-2">Software Engineer & AI Engineer</h3>
                    <p className="text-gray-400">Audace Labs, Chandigarh (Hybrid)</p>
                  </div>
                  <div className="text-gray-500 md:text-right mt-2 md:mt-0">
                    <p className="font-semibold">Dec 2024 - Aug 2025</p>
                  </div>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                    <span>Built Twitter AI agents for crypto engagement and real-time monitoring bots with automated actions and hashtag targeting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                    <span>Developed AI recruiter workflows and HR dashboard for job management, candidate tracking, and Facebook campaigns</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                    <span>Optimized LLMs via fine-tuning/merging (QLoRA, PEFT, MergeKit) and presented decentralized LLM marketplace research at WorldS4 2025, London, UK</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                    <span>Engineered Solana blockchain tools including CLI analyzers, tip wallet rebalancer, payout calculator, MEV dashboard, and dynamic USD/SOL features</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0f0f0f] border-2 border-cyan-500/40 rounded-2xl p-8 hover:border-cyan-500/70 transition-all duration-300 shadow-2xl shadow-cyan-900/20 hover:shadow-cyan-500/30"
                whileHover={{ scale: 1.01, y: -5 }}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-cyan-400 mb-2">Founding Software Engineer</h3>
                    <p className="text-gray-400">CheckMed, Remote</p>
                  </div>
                  <div className="text-gray-500 md:text-right mt-2 md:mt-0">
                    <p className="font-semibold">Feb 2024 - Aug 2024</p>
                  </div>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                    <span>Directed development of QR payment module with Razorpay integration, boosting transaction success rates by 10% and improving co-payment handling efficiency by 25%</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                    <span>Built backend APIs for QR payment and Camp modules, increasing system reliability by 20%</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                    <span>Conducted backend testing and debugging using Jest and Postman, achieving 95% uptime</span>
                  </li>
                </ul>
              </motion.div>
              
              <motion.div
                className="bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0f0f0f] border-2 border-orange-500/40 rounded-2xl p-8 hover:border-orange-500/70 transition-all duration-300 shadow-2xl shadow-orange-900/20 hover:shadow-orange-500/30"
                whileHover={{ scale: 1.01, y: -5 }}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-orange-400 mb-2">Software Engineer</h3>
                    <p className="text-gray-400">Calsoft Inc, Pune</p>
                  </div>
                  <div className="text-gray-500 md:text-right mt-2 md:mt-0">
                    <p className="font-semibold">Jan 2023 - Feb 2024</p>
                  </div>
                </div>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                    <span>Directed AWS and Linux system operations, leading to a 20% increase in storage efficiency and reduced downtime</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                    <span>Managed over 20+ project tasks with Jira, enhancing project completion efficiency by 25%</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                    <span>Conducted resilience, functional, and performance testing, increasing software quality by 25%</span>
                  </li>
                </ul>
              </motion.div>
            </div>
            
            {/* Research & Education */}
            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <Card className="bg-[#1a1a1a] border-2 border-purple-500/30 hover:border-purple-500/60 transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-purple-400">Research & Publications</h3>
                  <ul className="space-y-4 text-gray-300">
                    <li className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">WorldS4 2025 Presentation</p>
                        <p className="text-sm text-gray-400">Presented research paper on "Decentralized LLM Marketplace" at WorldS4 2025, London, UK (Virtual)</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-cyan-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Springer Publication (Upcoming)</p>
                        <p className="text-sm text-gray-400">Research paper scheduled for publication in Springer within few months</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-orange-500 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">LLM Optimization Research</p>
                        <p className="text-sm text-gray-400">LLM fine-tuning, merging, and distillation using QLoRA, PEFT, MergeKit on Google Colab GPU T4</p>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="bg-[#1a1a1a] border-2 border-emerald-500/30 hover:border-emerald-500/60 transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-emerald-400">Education & Growth</h3>
                  <div className="space-y-4 text-gray-300">
                    <div>
                      <p className="font-semibold text-lg">Bachelor of Engineering</p>
                      <p className="text-gray-400">Computer Science Engineering - 92%</p>
                      <p className="text-sm text-gray-500">Chitkara University, Rajpura (Aug 2019 - Jul 2023)</p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-gray-800">
                      <p className="text-gray-400 leading-relaxed">
                        Day by day learning and upskilling in AI, Gen AI, new languages and frameworks. Always eager with hunger to learn, dedicated and committed to excellence in software development and innovation.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/50">Continuous Learner</Badge>
                        <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/50">Dedicated</Badge>
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">Committed</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-32 px-4 relative z-10" data-testid="skills-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold mb-12 text-center bg-gradient-to-r from-emerald-400 to-orange-400 bg-clip-text text-transparent">
              Technical Skills
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(skills).map(([category, items], idx) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="bg-[#1a1a1a] border-gray-800 hover:border-emerald-500/50 transition-all duration-300 h-full">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-4 text-emerald-400">{category}</h3>
                      <div className="flex flex-wrap gap-2">
                        {items.map((skill) => (
                          <Badge
                            key={skill}
                            className="bg-[#0a0a0a] text-gray-300 hover:bg-emerald-500/20 hover:text-emerald-300 border border-gray-800 hover:border-emerald-500 transition-all cursor-pointer"
                            onMouseEnter={() => setHoveredSkill(skill)}
                            onMouseLeave={() => setHoveredSkill(null)}
                          >
                            {skill}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 px-4 relative z-10" data-testid="projects-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold mb-4 text-center bg-gradient-to-r from-cyan-400 to-orange-400 bg-clip-text text-transparent">
              Featured Projects
            </h2>
            <p className="text-center text-gray-400 mb-16 text-lg">Building innovative solutions across Blockchain, AI, and Web3</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, idx) => {
                const Icon = project.icon;
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    onClick={() => setSelectedProject(project)}
                    className="cursor-pointer"
                    data-testid={`project-card-${project.id}`}
                  >
                    <Card 
                      className="bg-[#1a1a1a] border-gray-800 hover:border-emerald-500/50 transition-all duration-300 group h-full flex flex-col hover:scale-105"
                    >
                      <CardContent className="p-6 flex flex-col h-full">
                        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${project.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <Badge className="w-fit mb-3 bg-[#0a0a0a] text-gray-400 border-gray-800">{project.category}</Badge>
                        <h3 className="text-xl font-semibold mb-3 text-gray-100 group-hover:text-emerald-400 transition-colors">
                          {project.title}
                        </h3>
                        <p className="text-gray-400 text-sm mb-4 flex-grow">{project.shortDesc}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {project.tech.slice(0, 3).map((tech) => (
                            <Badge key={tech} className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                              {tech}
                            </Badge>
                          ))}
                          {project.tech.length > 3 && (
                            <Badge className="text-xs bg-gray-800 text-gray-400 border-gray-700">
                              +{project.tech.length - 3}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold group-hover:text-emerald-300">
                          <span>View Details</span>
                          <ExternalLink className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-32 px-4 relative z-10" data-testid="certifications-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold mb-12 text-center bg-gradient-to-r from-orange-400 to-emerald-400 bg-clip-text text-transparent">
              Certifications & Learning
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {certifications.map((cert, idx) => (
                <motion.div
                  key={cert}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="bg-[#1a1a1a] border-gray-800 hover:border-orange-500/50 transition-all duration-300">
                    <CardContent className="p-6 flex items-start gap-4">
                      <Award className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
                      <p className="text-gray-300">{cert}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <motion.p
              className="text-center text-gray-400 mt-8 text-lg"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              Continuously upskilling in AI, Gen AI, and emerging technologies with dedication and hunger to learn
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 relative z-10" data-testid="contact-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Let's Connect
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Open to opportunities and collaborations in Blockchain, AI, and Full-Stack Development
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a href="mailto:varunsharma6956@gmail.com">
                <Button 
                  size="lg"
                  className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white border-0 text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/50 w-full sm:w-auto"
                  data-testid="contact-email-btn"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email Me
                </Button>
              </a>
              
              <a href="https://www.linkedin.com/in/varun-sharma-00a2b518a/" target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                  data-testid="contact-linkedin-btn"
                >
                  <Linkedin className="w-5 h-5 mr-2" />
                  LinkedIn
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-900 relative z-10">
        <div className="max-w-6xl mx-auto text-center text-gray-500">
          <p>© 2025 Varun Sharma. All rights reserved.</p>
          <p className="mt-2">Backend/Python Developer, AI Engineer & Full Stack AI Engineer</p>
        </div>
      </footer>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
            <DialogContent 
              className="w-[90vw] sm:w-[80vw] md:w-[65vw] lg:w-[50vw] max-w-3xl bg-[#1a1a1a] border-2 border-emerald-500/30 text-gray-100 shadow-2xl shadow-emerald-900/30 p-0 overflow-hidden" 
              data-testid="project-modal"
            >
              {/* Fixed Header with Close Button */}
              <div className="sticky top-0 bg-gradient-to-b from-[#1a1a1a] to-[#1a1a1a]/95 z-20 px-6 pt-6 pb-3 border-b border-gray-800/50">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center transition-all hover:scale-110"
                  data-testid="close-modal-btn"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-300" />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${selectedProject.gradient} flex items-center justify-center shadow-xl flex-shrink-0`}>
                    {selectedProject.icon && <selectedProject.icon className="w-7 h-7 text-white" />}
                  </div>
                  <div className="flex-1 pr-12">
                    <h2 className="text-2xl font-bold text-gray-100 mb-1">{selectedProject.title}</h2>
                    <p className="text-gray-400 text-sm">{selectedProject.category}</p>
                  </div>
                </div>
              </div>
              
              {/* Scrollable Content */}
              <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6 py-4">
                <div className="space-y-5">
                  {selectedProject.overview && (
                    <div>
                      <h4 className="text-lg font-semibold mb-2 text-emerald-400 flex items-center gap-2">
                        <div className="w-1 h-5 bg-emerald-500 rounded"></div>
                        Project Overview
                      </h4>
                      <p className="text-gray-300 leading-relaxed text-sm pl-3">{selectedProject.overview}</p>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-cyan-400 flex items-center gap-2">
                      <div className="w-1 h-5 bg-cyan-500 rounded"></div>
                      Description
                    </h4>
                    <p className="text-gray-300 leading-relaxed text-sm pl-3">{selectedProject.fullDesc}</p>
                  </div>
                  
                  {selectedProject.achievements && selectedProject.achievements.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-2 text-orange-400 flex items-center gap-2">
                        <div className="w-1 h-5 bg-orange-500 rounded"></div>
                        Key Achievements
                      </h4>
                      <ul className="space-y-2 pl-3">
                        {selectedProject.achievements.map((achievement, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-300 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 flex-shrink-0"></div>
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedProject.features && selectedProject.features.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-2 text-purple-400 flex items-center gap-2">
                        <div className="w-1 h-5 bg-purple-500 rounded"></div>
                        Key Features
                      </h4>
                      <ul className="space-y-2 pl-3">
                        {selectedProject.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-300 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-lg font-semibold mb-2 text-emerald-400 flex items-center gap-2">
                      <div className="w-1 h-5 bg-emerald-500 rounded"></div>
                      Technologies Used
                    </h4>
                    <div className="flex flex-wrap gap-2 pl-3">
                      {selectedProject.tech.map((tech) => (
                        <Badge key={tech} className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 transition-all text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Portfolio />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;